"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useCart, type Passenger } from "@/context/CartContext";
import { ChevronLeft } from "lucide-react";
import { apiConfig } from "@/config/apiConfig";

function parseDateTime(dateStr?: string, timeStr?: string): { date: string; time: string } {
  try {
    if (!dateStr && !timeStr) {
      return { date: 'TBD', time: '' };
    }

    let formattedDate = 'TBD';
    let formattedTime = '';

    if (dateStr && typeof dateStr === 'string' && dateStr.trim().length > 0) {
      try {
        if (dateStr.length >= 8 && /^\d{8}/.test(dateStr)) {
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          
          if (parseInt(month) >= 1 && parseInt(month) <= 12 && parseInt(day) >= 1 && parseInt(day) <= 31) {
            const dateObj = new Date(`${year}-${month}-${day}T00:00:00Z`);
            if (!isNaN(dateObj.getTime())) {
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              formattedDate = `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
            }
          }
        }
      } catch (e) {
        formattedDate = dateStr || 'TBD';
      }
    }

    if (timeStr && typeof timeStr === 'string' && timeStr.trim().length >= 4) {
      try {
        const timeClean = timeStr.trim();
        if (/^\d{4}/.test(timeClean)) {
          const hours = timeClean.substring(0, 2);
          const minutes = timeClean.substring(2, 4);
          
          if (parseInt(hours) >= 0 && parseInt(hours) < 24 && parseInt(minutes) >= 0 && parseInt(minutes) < 60) {
            formattedTime = `${hours}:${minutes}`;
          }
        }
      } catch (e) {
        // ignore
      }
    }

    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    return { date: 'TBD', time: '' };
  }
}

export function BookingConfirmation() {
  const { cart, setIsCheckout, passengers, setPassengers, searchPassengers, sessionId } = useCart();
  const firstService = cart[0];
  
  const [currentStep, setCurrentStep] = useState<"passenger-details" | "payment">("passenger-details");
  const [email, setEmail] = useState("");
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContactTelephone, setEmergencyContactTelephone] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passengerErrors, setPassengerErrors] = useState<Record<number, Record<string, string>>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<{ status: "processing" | "success" | "failed"; message: string } | null>(null);

  // Listen for messages from PayGate iframe
  useEffect(() => {
    const handleIframeMessage = (event: MessageEvent) => {
      // Accept messages from localhost
      if (!event.origin.includes('localhost')) return;
      
      if (event.data?.type === 'PAYMENT_RETURN') {
        console.log('✅ Payment return page loaded in iframe');
      }
      
      if (event.data?.type === 'CLOSE_PAYMENT_IFRAME') {
        console.log('✅ PayGate signaled payment complete - closing modal');
        setShowPaymentModal(false);
        setPaymentStatus({ status: 'success', message: 'Payment submitted successfully! Your booking is being confirmed.' });
        
        // Auto-close success message after 3 seconds
        setTimeout(() => {
          setIsProcessing(false);
          setIsCheckout(false);
          setCurrentStep("passenger-details");
        }, 3000);
      }
    };
    
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, []);

  // Initialize passengers on component mount
  useEffect(() => {
    if (passengers.length === 0 && searchPassengers) {
      const totalPassengers = searchPassengers.adultsCount + searchPassengers.childrenCount;
      const newPassengers: Passenger[] = [];
      
      let passengerIndex = 0;
      
      // Add adults
      for (let i = 0; i < searchPassengers.adultsCount; i++) {
        newPassengers.push({
          passengerIndex: passengerIndex++,
          passengerType: "Adult",
          title: "",
          surname: "",
          initials: "",
          idPassportCountry: "",
          cellphone: "",
          email: "",
          emergencyContactName: "",
          emergencyContactTelephone: "",
        });
      }
      
      // Add children
      for (let i = 0; i < searchPassengers.childrenCount; i++) {
        newPassengers.push({
          passengerIndex: passengerIndex++,
          passengerType: "Child",
          title: "",
          surname: "",
          initials: "",
          idPassportCountry: "",
          cellphone: "",
          email: "",
          emergencyContactName: "",
          emergencyContactTelephone: "",
        });
      }
      
      setPassengers(newPassengers);
    }
  }, [searchPassengers, passengers.length, setPassengers]);

  const updatePassenger = (index: number, field: keyof Passenger, value: any) => {
    const updatedPassengers = [...passengers];
    updatedPassengers[index] = {
      ...updatedPassengers[index],
      [field]: value,
    };
    setPassengers(updatedPassengers);
  };

  const validatePassengerForm = () => {
    const newPassengerErrors: Record<number, Record<string, string>> = {};
    const tripErrors: Record<string, string> = {};

    // Validate each passenger
    passengers.forEach((passenger, index) => {
      const pErrors: Record<string, string> = {};
      
      if (!passenger.title.trim()) pErrors.title = "Title is required";
      if (!passenger.surname.trim()) pErrors.surname = "Surname is required";
      if (!passenger.initials.trim()) pErrors.initials = "Initials are required";
      if (!passenger.idPassportCountry.trim()) pErrors.idPassportCountry = "ID/Passport country is required";
      if (!passenger.cellphone.trim()) pErrors.cellphone = "Cellphone is required";
      
      if (Object.keys(pErrors).length > 0) {
        newPassengerErrors[index] = pErrors;
      }
    });

    // Validate trip-level fields
    if (!email.trim()) tripErrors.email = "Email is required";
    if (email && !email.includes("@")) tripErrors.email = "Valid email is required";
    if (!emergencyContactName.trim()) tripErrors.emergencyContactName = "Emergency contact name is required";
    if (!emergencyContactTelephone.trim()) tripErrors.emergencyContactTelephone = "Emergency contact telephone is required";

    setPassengerErrors(newPassengerErrors);
    setErrors(tripErrors);
    
    return Object.keys(newPassengerErrors).length === 0 && Object.keys(tripErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validatePassengerForm()) {
      // Update all passengers with trip-level info
      const updatedPassengers = passengers.map(p => ({
        ...p,
        email,
        emergencyContactName,
        emergencyContactTelephone,
      }));
      setPassengers(updatedPassengers);

      // PHASE 1: Save passengers to database before moving to payment
      const savePassengers = async () => {
        if (!sessionId) {
          console.warn('⚠️  Session ID not available - passengers will not be persisted');
          setCurrentStep("payment");
          return;
        }

        try {
          console.log(`\n💾 PHASE 1: Saving ${updatedPassengers.length} passengers to database...`);
          
          // Build passengers array in correct format for backend
          const passengersForBackend = updatedPassengers.map((p, idx) => ({
            title: p.title || 'Mr',
            firstName: p.surname?.split(' ')[0] || p.surname || '', // First word of surname
            lastName: p.surname || '',
            idNumber: p.idPassportCountry || '',
            phone: p.cellphone || '',
            email: p.email || '',
            initials: p.initials || '',
            passportNumber: p.idPassportCountry || ''
          }));

          // Call broker service directly
          const result = await apiConfig.endpoints.addPassenger({
            sessionId,
            serviceNumber: cart[0]?.service_number || '',
            passengers: passengersForBackend
          });

          if (result) {
            console.log(`   ✅ All passengers saved`);
          }
        } catch (error) {
          console.error('   ⚠️  Passenger persistence error (non-critical):', error);
        }
      };

      savePassengers().then(() => {
        setCurrentStep("payment");
      });
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsProcessing(true);
    setPaymentStatus({ status: "processing", message: "Initiating payment..." });
    
    try {
      // Calculate total fare
      const totalFare = cart.reduce((sum, service) => {
        return sum + (service.fare ? parseFloat(service.fare.toString()) : 0);
      }, 0) * passengers.length;
      
      const paymentReference = (cart[0]?.service_number || "BK" + Date.now()).toString().trim();
      const globalRefId = `${paymentReference}-${Date.now()}`;

      // PHASE 2: Calculate fare verification before payment
      if (sessionId) {
        try {
          console.log(`\n💰 PHASE 2: Calculating and verifying fare...`);
          const fareResponse = await apiConfig.endpoints.calculateFare({
            sessionId,
            serviceNumber: cart[0]?.service_number || '',
            adults: passengers.length,
            children: 0
          });

          if (!fareResponse) {
            console.warn('   ⚠️  Fare calculation warning (non-critical)');
          } else {
            console.log('   ✅ Fare calculated and verified');
          }
        } catch (fareError) {
          console.warn('   ⚠️  Fare verification skipped:', fareError);
          // Non-critical - continue to payment
        }
      }
      
      console.log("Calling backend to initiate PayGate payment for:", {
        amount: totalFare,
        passengers: passengers.length,
        reference: paymentReference,
        globalRefId
      });
      
      // Call Node.js backend to initiate PayGate payment
      // Initiate payment with broker service
      const paymentData = await apiConfig.endpoints.initiatePayment({
        sessionId,
        serviceNumber: cart[0]?.service_number || '',
        amount: totalFare,
        reference: globalRefId
      });
      const initiateResponse = await fetch(backendUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reference: paymentReference,
          amount: totalFare,
          globalRefId,
          sessionId
        })
      });

      if (!initiateResponse.ok) {
        const errorData = await initiateResponse.json();
        throw new Error(errorData.error || 'Failed to initiate payment');
      }

      const initiateData = await initiateResponse.json();
      const { payRequestId, checksum } = initiateData.data;

      console.log("✅ Got PayGate tokens from backend:", {
        payRequestId: payRequestId?.substring(0, 8) + '...',
        checksum: checksum?.substring(0, 8) + '...'
      });

      // PHASE 2: Log payment attempt
      if (sessionId) {
        try {
          console.log('📝 PHASE 2: Logging payment attempt...');
          await fetch(apiConfig.endpoints.logPayment, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              globalRefId,
              amount: totalFare,
              paymentReference,
              payRequestId,
              passengers: passengers.length,
              status: 'INITIATED'
            })
          });
          console.log('   ✅ Payment logged');
        } catch (logError) {
          console.warn('   ⚠️  Payment logging skipped:', logError);
          // Non-critical
        }
      }
      
      // Create form for PayGate process.trans submission (following ASPX PaymentPage.aspx pattern)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://secure.paygate.co.za/payweb3/process.trans';
      form.target = 'paygate_iframe'; // Named iframe in modal
      form.style.display = 'none';
      
      // Create hidden input fields for form data (POST parameters, not URL)
      const payRequestField = document.createElement('input');
      payRequestField.type = 'hidden';
      payRequestField.name = 'PAY_REQUEST_ID';
      payRequestField.value = payRequestId;
      form.appendChild(payRequestField);
      
      const checksumField = document.createElement('input');
      checksumField.type = 'hidden';
      checksumField.name = 'CHECKSUM';
      checksumField.value = checksum.toUpperCase();
      form.appendChild(checksumField);
      
      // Add form to DOM
      document.body.appendChild(form);
      
      // Show payment modal FIRST so iframe exists
      setShowPaymentModal(true);
      setPaymentStatus({ status: 'processing', message: 'Loading payment gateway...' });
      
      // Wait a tick for iframe to be created, then submit
      setTimeout(() => {
        console.log("Submitting payment form to PayGate process.trans via iframe");
        form.submit();
        
        // Cleanup form after submission
        try {
          document.body.removeChild(form);
        } catch (e) {
          // Already removed
        }
      }, 100);

      // PHASE 2: Call confirm endpoint to mark seats as SOLD
      if (sessionId && firstService) {
        try {
          console.log('\n✅ PHASE 2: Marking seats as SOLD...');
          const confirmResponse = await fetch(apiConfig.endpoints.confirm, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              globalRefId,
              serviceNumber: firstService.service_number,
              departureDate: firstService.board_date,
              returnDate: searchPassengers?.returnDate
            })
          });

          if (!confirmResponse.ok) {
            console.warn('   ⚠️  Confirmation warning:', confirmResponse.statusText);
          } else {
            console.log('   ✅ Booking confirmed - seats marked as SOLD');
          }
        } catch (confirmError) {
          console.warn('   ⚠️  Booking confirmation skipped:', confirmError);
        }
      }
      
      // Poll backend to check if payment was successful
      const pollPaymentStatus = setInterval(async () => {
        try {
          const statusResponse = await apiConfig.endpoints.paymentStatus(globalRefId);
          
          if (statusResponse.ok) {
            const status = await statusResponse.json();
            if (status.paid === true) {
              clearInterval(pollPaymentStatus);
              setPaymentStatus({ status: 'success', message: 'Payment confirmed! Booking complete.' });
              // Auto-close after 3 seconds
              setTimeout(() => {
                setShowPaymentModal(false);
                setIsProcessing(false);
                setIsCheckout(false);
                setCurrentStep("passenger-details");
              }, 3000);
            } else if (status.failed === true) {
              clearInterval(pollPaymentStatus);
              setPaymentStatus({ status: 'failed', message: status.message || 'Payment declined. Please try again.' });
            }
          }
        } catch (e) {
          console.log('Payment status check...', e.message);
        }
      }, 2000); // Poll every 2 seconds
      
      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollPaymentStatus);
      }, 300000);
      
    } catch (error) {
      console.error("Payment error:", error);
      setPaymentStatus({
        status: "failed",
        message: "Payment initiation failed: " + (error instanceof Error ? error.message : "Unknown error")
      });
      setShowPaymentModal(true);
      setIsProcessing(false);
    }
  };

  if (!firstService || passengers.length === 0) {
    return null;
  }

  const boardDateTime = parseDateTime(firstService.board_date, firstService.board_time);
  const destDateTime = parseDateTime(firstService.destination_date, firstService.destination_time);
  
  // Get return service if it's a return trip
  const returnService = searchPassengers?.tripType === "return" && cart.length > 1 ? cart[1] : null;
  const returnBoardDateTime = returnService ? parseDateTime(returnService.board_date, returnService.board_time) : null;
  const returnDestDateTime = returnService ? parseDateTime(returnService.destination_date, returnService.destination_time) : null;

  return (
    <div className="w-full">
      {/* Step Indicator */}
      <div className="mb-6 flex gap-2">
        <div className={`flex-1 h-1 rounded ${currentStep === "passenger-details" ? "bg-primary" : "bg-primary/30"}`} />
        <div className={`flex-1 h-1 rounded ${currentStep === "payment" ? "bg-primary" : "bg-primary/30"}`} />
      </div>

      {/* Header Button */}
      <div className="mb-6">
        <Button
          onClick={() => setIsCheckout(false)}
          variant="ghost"
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Cart
        </Button>
      </div>

      {/* Trip Summary Card */}
      <Card className="bg-card rounded-2xl p-6 md:p-8 border border-border/50 mb-6">
        <h2 className="text-2xl font-bold mb-6">OUTBOUND TRIP</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Departure */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Departure Point</h3>
            <p className="text-base font-semibold mb-4">
              {firstService.board_description || 'TBD'}
              {firstService.boardlocat && <span className="block text-sm text-muted-foreground">{firstService.boardlocat}</span>}
            </p>
            
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Departure Date & Time</h3>
            <p className="text-lg font-bold">
              {boardDateTime.date} {boardDateTime.time}
            </p>
          </div>

          {/* Arrival */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Arrival Point</h3>
            <p className="text-base font-semibold mb-4">
              {firstService.destination_description || 'TBD'}
              {firstService.destlocat && <span className="block text-sm text-muted-foreground">{firstService.destlocat}</span>}
            </p>
            
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Arrival Date & Time</h3>
            <p className="text-lg font-bold">
              {destDateTime.date} {destDateTime.time}
            </p>
          </div>
        </div>

        {/* Carrier */}
        <div className="mt-6 pt-6 border-t border-border/30">
          <h3 className="text-sm font-semibold text-muted-foreground mb-2">Carrier</h3>
          <p className="text-lg font-bold">{firstService.carrier_name || firstService.carrier}</p>
        </div>
      </Card>

      {/* Return Trip Card - Only show if return trip */}
      {returnService && returnBoardDateTime && returnDestDateTime && (
        <Card className="bg-card rounded-2xl p-6 md:p-8 border border-border/50 mb-6">
          <h2 className="text-2xl font-bold mb-6">RETURN TRIP</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Departure */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Departure Point</h3>
              <p className="text-base font-semibold mb-4">
                {returnService.board_description || 'TBD'}
                {returnService.boardlocat && <span className="block text-sm text-muted-foreground">{returnService.boardlocat}</span>}
              </p>
              
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Departure Date & Time</h3>
              <p className="text-lg font-bold">
                {returnBoardDateTime.date} {returnBoardDateTime.time}
              </p>
            </div>

            {/* Arrival */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Arrival Point</h3>
              <p className="text-base font-semibold mb-4">
                {returnService.destination_description || 'TBD'}
                {returnService.destlocat && <span className="block text-sm text-muted-foreground">{returnService.destlocat}</span>}
              </p>
              
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Arrival Date & Time</h3>
              <p className="text-lg font-bold">
                {returnDestDateTime.date} {returnDestDateTime.time}
              </p>
            </div>
          </div>

          {/* Carrier */}
          <div className="mt-6 pt-6 border-t border-border/30">
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Carrier</h3>
            <p className="text-lg font-bold">{returnService.carrier_name || returnService.carrier}</p>
          </div>
        </Card>
      )}

      {/* PASSENGER DETAILS FORM - Step 1 */}
      {currentStep === "passenger-details" && (
      <Card className="bg-card rounded-2xl p-6 md:p-8 border border-border/50 mb-6">
        <h2 className="text-2xl font-bold mb-6">Passenger Information</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Passenger Cards */}
          <div className="space-y-4">
            {passengers.map((passenger, index) => (
              <Card key={index} className="bg-muted/30 p-6 border border-border/50">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">
                    Passenger {index + 1} - {passenger.passengerType}
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Title *</label>
                    <select
                      title="Select title"
                      value={passenger.title}
                      onChange={(e) => updatePassenger(index, "title", e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-card"
                    >
                      <option value="">Select title</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                      <option value="Prof">Prof</option>
                    </select>
                    {passengerErrors[index]?.title && <p className="text-xs text-destructive mt-1">{passengerErrors[index].title}</p>}
                  </div>

                  {/* Initials */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Initials *</label>
                    <Input
                      value={passenger.initials}
                      onChange={(e) => updatePassenger(index, "initials", e.target.value.toUpperCase())}
                      placeholder="e.g., JD"
                      maxLength={3}
                    />
                    {passengerErrors[index]?.initials && <p className="text-xs text-destructive mt-1">{passengerErrors[index].initials}</p>}
                  </div>

                  {/* Surname */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Surname *</label>
                    <Input
                      value={passenger.surname}
                      onChange={(e) => updatePassenger(index, "surname", e.target.value)}
                      placeholder="e.g., Smith"
                    />
                    {passengerErrors[index]?.surname && <p className="text-xs text-destructive mt-1">{passengerErrors[index].surname}</p>}
                  </div>

                  {/* ID/Passport Country */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">ID / Passport Country *</label>
                    <Input
                      value={passenger.idPassportCountry}
                      onChange={(e) => updatePassenger(index, "idPassportCountry", e.target.value)}
                      placeholder="e.g., South Africa"
                    />
                    {passengerErrors[index]?.idPassportCountry && <p className="text-xs text-destructive mt-1">{passengerErrors[index].idPassportCountry}</p>}
                  </div>

                  {/* Cellphone */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Cellphone *</label>
                    <Input
                      value={passenger.cellphone}
                      onChange={(e) => updatePassenger(index, "cellphone", e.target.value)}
                      placeholder="e.g., +27123456789"
                    />
                    {passengerErrors[index]?.cellphone && <p className="text-xs text-destructive mt-1">{passengerErrors[index].cellphone}</p>}
                  </div>

                  {/* Passenger Type (Display Only) */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Passenger Type</label>
                    <div className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-sm">
                      {passenger.passengerType}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Trip Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Trip Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold mb-2">Email address for payment reference *</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Emergency Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Emergency Contact Name */}
              <div>
                <label className="block text-sm font-semibold mb-2">Name of contact person for emergency purposes *</label>
                <Input
                  value={emergencyContactName}
                  onChange={(e) => setEmergencyContactName(e.target.value)}
                  placeholder="Emergency contact name"
                />
                {errors.emergencyContactName && <p className="text-xs text-destructive mt-1">{errors.emergencyContactName}</p>}
              </div>

              {/* Emergency Contact Telephone */}
              <div>
                <label className="block text-sm font-semibold mb-2">Telephone number of emergency contact person *</label>
                <Input
                  value={emergencyContactTelephone}
                  onChange={(e) => setEmergencyContactTelephone(e.target.value)}
                  placeholder="Telephone number"
                />
                {errors.emergencyContactTelephone && <p className="text-xs text-destructive mt-1">{errors.emergencyContactTelephone}</p>}
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCheckout(false)}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              type="submit"
              className="flex-1"
            >
              Proceed to Payment
            </Button>
          </div>
        </form>
      </Card>
      )}

      {/* PAYMENT FORM - Step 2 */}
      {currentStep === "payment" && (
        <Card className="bg-card rounded-2xl p-6 md:p-8 border border-border/50">
          <h2 className="text-2xl font-bold mb-6">Payment Confirmation</h2>
          
          {/* Order Summary */}
          <div className="mb-8 space-y-4 pb-6 border-b border-border/30">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Trip Details:</span>
              <span className="font-medium">
                {cart.length} service(s) × {passengers.length} passenger(s)
              </span>
            </div>
            
            {cart.map((service, idx) => (
              <div key={idx} className="text-sm">
                <p className="font-medium">
                  {service.board_description} → {service.destination_description}
                </p>
                <p className="text-xs text-muted-foreground">
                  Service: {service.service_number} | 
                  Departure: {service.board_date} at {service.board_time}
                </p>
              </div>
            ))}
          </div>

          {/* Total Amount - HIGHLIGHTED */}
          <div className="mb-8 p-6 bg-primary/10 rounded-2xl border-2 border-primary/30">
            <p className="text-sm text-muted-foreground mb-2">Total Amount Due</p>
            <p className="text-4xl font-bold text-primary mb-1">
              R {(cart.reduce((sum, service) => sum + (parseFloat(service.fare?.toString() || "0")), 0) * passengers.length).toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground">
              {passengers.length} × R{(cart.reduce((sum, service) => sum + (parseFloat(service.fare?.toString() || "0")), 0)).toFixed(2)}
            </p>
          </div>

          {/* Passenger Summary */}
          <div className="mb-8 pb-6 border-b border-border/30">
            <h3 className="font-semibold mb-3">Passengers ({passengers.length})</h3>
            <div className="space-y-2">
              {passengers.map((p, idx) => (
                <div key={idx} className="text-sm p-2 bg-muted/30 rounded">
                  <p className="font-medium">{p.surname}, {p.initials}</p>
                  <p className="text-xs text-muted-foreground">{p.passengerType} • Email: {email}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Info Text */}
          <div className="mb-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-900 dark:text-blue-200">
              <strong>Secure Payment:</strong> A secure payment window will open in a modal below. Enter your card details to complete your payment. The modal will automatically close once payment is confirmed.
            </p>
          </div>

          {/* Buttons */}
          <form onSubmit={handlePaymentSubmit} className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentStep("passenger-details")}
              className="flex-1"
              disabled={isProcessing}
            >
              Back to Details
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-primary hover:bg-primary/90"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Proceed to Payment"}
            </Button>
          </form>
        </Card>
      )}

      {/* Payment Modal Overlay */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          {/* During Processing: Show PayGate iframe */}
          {paymentStatus?.status === "processing" && (
            <Card className="w-full h-full max-h-screen max-w-4xl p-0 overflow-hidden flex flex-col">
              <div className="flex-1 relative bg-white dark:bg-black">
                {/* PayGate iframe - large and clickable */}
                <iframe
                  name="paygate_iframe"
                  className="w-full h-full border-0"
                  title="PayGate Payment"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                  style={{ colorScheme: 'light' }}
                />
              </div>
              <div className="p-4 border-t border-border flex justify-between items-center bg-muted/30">
                <p className="text-xs text-muted-foreground">Secure payment powered by PayGate</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      console.log('✅ Payment completed - closing modal');
                      setShowPaymentModal(false);
                      setPaymentStatus(null);
                      setIsProcessing(false);
                      setIsCheckout(false);
                      setCurrentStep("passenger-details");
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Payment Done
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Success/Failed states in smaller card */}
          {(paymentStatus?.status === "success" || paymentStatus?.status === "failed") && (
            <Card className="max-w-md w-full p-6">
              {paymentStatus.status === "success" && (
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-green-600">Payment Successful</h3>
                  <p className="text-sm">{paymentStatus.message}</p>
                  <Button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setIsCheckout(false);
                      setCurrentStep("passenger-details");
                    }}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    Continue
                  </Button>
                </div>
              )}

              {paymentStatus.status === "failed" && (
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-red-600">Payment Failed</h3>
                  <p className="text-sm text-muted-foreground">{paymentStatus.message}</p>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={() => {
                        setShowPaymentModal(false);
                        setPaymentStatus(null);
                        setIsProcessing(false);
                        // Restart the payment process
                        setTimeout(() => handlePaymentSubmit(new Event('submit')), 500);
                      }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      Try Again
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPaymentModal(false);
                        setPaymentStatus(null);
                        setIsProcessing(false);
                        setCurrentStep("passenger-details");
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
